import { useEffect, useState } from 'react'

// PWA install offering (contextual, never auto-prompted — the browser's own
// mini-infobar is suppressed by capturing beforeinstallprompt). Chrome/Edge/
// Android expose the event; iOS/Firefox never do, so surfaces fall back to a
// how-to sheet. Install matters doubly on iOS: web push only works installed.

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault() // suppress the mini-infobar; we offer it in-context
    deferredPrompt = e as BeforeInstallPromptEvent
    listeners.forEach((l) => l())
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    listeners.forEach((l) => l())
  })
}

export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  )
}

export function isIOS(): boolean {
  return typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent)
}

export function useInstall() {
  const [, bump] = useState(0)
  useEffect(() => {
    const l = () => bump((n) => n + 1)
    listeners.add(l)
    return () => {
      listeners.delete(l)
    }
  }, [])

  return {
    installed: isInstalled(),
    /** True when the native prompt is available (Chrome/Edge/Android). */
    canPrompt: deferredPrompt !== null,
    ios: isIOS(),
    /** Fire the native prompt; resolves true when accepted. */
    async promptInstall(): Promise<boolean> {
      if (!deferredPrompt) return false
      const p = deferredPrompt
      deferredPrompt = null
      await p.prompt()
      const choice = await p.userChoice
      return choice.outcome === 'accepted'
    },
  }
}
